import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

const routes = (window.Theme && window.Theme.routes) || {};
const cartAddUrl = routes.cart_add_url || '/cart/add.js';
const cartUrl = routes.cart_url || '/cart';

function openCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return false;
  if (typeof drawer.open === 'function') {
    drawer.open();
    return true;
  }
  if (window.customElements) {
    customElements.whenDefined('theme-drawer').then(() => {
      const next = document.getElementById('cart-drawer');
      if (next && typeof next.open === 'function') next.open();
    });
    return true;
  }
  return false;
}

async function addItemsToCart(items) {
  const drawerSectionId = document.querySelector('cart-items-component')?.dataset.sectionId;
  const formData = new FormData();
  items.forEach((item, index) => {
    formData.append(`items[${index}][id]`, String(item.id));
    formData.append(`items[${index}][quantity]`, String(item.quantity));
  });
  if (drawerSectionId) formData.set('sections', drawerSectionId);

  let deferred = null;
  try {
    deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: deferred.promise,
      })
    );
  } catch (e) {
    deferred = null;
  }

  const cfg = fetchConfig('javascript', { body: formData });
  const res = await fetch(cartAddUrl, cfg);
  let data = {};
  try {
    data = await res.json();
  } catch (e) {
    data = {};
  }

  if (!res.ok || data.status) {
    const msg = data.description || data.message || 'This item is currently unavailable.';
    if (deferred) {
      try {
        deferred.reject(new Error(msg));
      } catch (e) {}
    }
    throw new Error(msg);
  }

  if (deferred) {
    try {
      const cart = await fetch(`${cartUrl}.json`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'h9-home',
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          sections: data.sections,
          didError: false,
        },
      });
    } catch (e) {
      try {
        deferred.resolve({ detail: { didError: false, sections: data.sections } });
      } catch (_) {}
    }
  }

  if (!openCartDrawer()) window.location.href = cartUrl;
}

function moneyFromCents(cents, template) {
  const value = (Number(cents) / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  return (template || '₹{{amount}}').replace(/\{\{\s*amount[^}]*\}\}/g, value);
}

function bindOnce(root) {
  if (!root || root.dataset.h9Bound === '1') return false;
  root.dataset.h9Bound = '1';
  return true;
}

function initHero(root) {
  if (!bindOnce(root)) return;
  const slides = root.querySelectorAll('[data-h9-slide]');
  const prevBtn = root.querySelector('[data-h9-hero-prev]');
  const nextBtn = root.querySelector('[data-h9-hero-next]');
  if (!slides.length) return;
  let i = 0;
  let timer;
  const show = (n) => {
    slides.forEach((s) => s.classList.toggle('is-active', Number(s.dataset.h9Slide) === n));
    i = n;
  };
  const auto = () => {
    timer = setInterval(() => show((i + 1) % slides.length), 4000);
  };
  const restart = (n) => {
    clearInterval(timer);
    show(n);
    auto();
  };
  prevBtn?.addEventListener('click', () => restart((i - 1 + slides.length) % slides.length));
  nextBtn?.addEventListener('click', () => restart((i + 1) % slides.length));
  auto();
}

function initShop(root) {
  if (!bindOnce(root)) return;
  const tabs = root.querySelector('[data-h9-tabs]');
  const cards = root.querySelectorAll('[data-h9-card]');
  const cta = root.querySelector('[data-h9-bundle-cta]');
  const bundles = JSON.parse(root.querySelector('[data-h9-moment-bundles]')?.textContent || '{}');

  const updateCta = (moment) => {
    if (!cta) return;
    const b = bundles[moment];
    cta.innerHTML = b
      ? `Want both? <a href="${b.href}">Shop the ${moment} bundle</a> — ${b.items}`
      : '';
  };

  const momentKey = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/&amp;/g, '&')
      .replace(/&/g, 'and')
      .replace(/\s+/g, ' ')
      .trim();

  const applyMoment = (moment) => {
    if (!moment) return;
    tabs?.querySelectorAll('[data-h9-moment]').forEach((t) => {
      t.classList.toggle('is-active', t.dataset.h9Moment === moment);
    });
    const selected = momentKey(moment);
    cards.forEach((card) => {
      const matches = selected === 'all' || momentKey(card.dataset.moment) === selected;
      card.classList.toggle('is-hidden', !matches);
    });
    updateCta(moment);
  };

  tabs?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-h9-moment]');
    if (!btn) return;
    applyMoment(btn.dataset.h9Moment);
  });

  const requested = new URLSearchParams(window.location.search).get('moment');
  if (requested) applyMoment(requested);
}

function initCompare(root) {
  if (!bindOnce(root)) return;
  const toggle = root.querySelector('[data-h9-compare-toggle]');
  const rows = root.querySelectorAll('[data-h9-compare-row]');
  const apply = (side) => {
    rows.forEach((row) => {
      const yes = row.dataset[side] === 'true';
      row.classList.toggle('is-yes', yes);
      row.classList.toggle('is-no', !yes);
      const mark = row.querySelector('.h9-mark');
      if (mark) mark.textContent = yes ? '✓' : '✕';
    });
  };
  apply('them');
  toggle?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-side]');
    if (!btn) return;
    toggle.querySelectorAll('[data-side]').forEach((b) => b.classList.remove('is-active'));
    btn.classList.add('is-active');
    apply(btn.dataset.side);
  });
}

function formatBundleRupees(amount) {
  return '₹' + Number(amount).toLocaleString('en-IN');
}

function initBundle(root) {
  if (!bindOnce(root)) return;
  const products = JSON.parse(root.querySelector('[data-h9-bundle-products]')?.textContent || '[]');
  const grid = root.querySelector('[data-h9-flavor-grid]');
  const amountEl = root.querySelector('[data-h9-bundle-amount]');
  const saveEl = root.querySelector('[data-h9-bundle-save]');
  const hintEl = root.querySelector('[data-h9-bundle-hint]');
  const addBtn = root.querySelector('[data-h9-bundle-add]');
  let bundleSize = Number(root.querySelector('.h9-pack-size.is-active')?.dataset.size || 3);
  let picked = [];

  const packMeta = {};
  root.querySelectorAll('[data-h9-pack]').forEach((btn) => {
    packMeta[btn.dataset.size] = {
      save: Number(btn.dataset.save || 0),
    };
  });

  const priceByIdRupees = new Map(products.map((p) => [String(p.id), Number(p.price_cents || 0) / 100]));
  const avgPriceRupees = products.length
    ? products.reduce((sum, p) => sum + Number(p.price_cents || 0), 0) / products.length / 100
    : 449;

  const renderGrid = () => {
    if (!grid) return;
    grid.innerHTML = products
      .map((p) => {
        const id = String(p.id);
        const pickedCls = picked.includes(id) ? 'is-picked' : '';
        const disabled = !picked.includes(id) && picked.length >= bundleSize ? 'is-disabled' : '';
        return `<button type="button" class="h9-flavor-pick ${pickedCls} ${disabled}" data-id="${id}">
          ${p.short}
        </button>`;
      })
      .join('');
    grid.querySelectorAll('.h9-flavor-pick').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.id;
        if (picked.includes(id)) picked = picked.filter((x) => x !== id);
        else if (picked.length < bundleSize) picked.push(id);
        update();
      });
    });
  };

  const update = () => {
    const meta = packMeta[bundleSize] || { save: 0 };
    const pickedTotal = picked.reduce((sum, id) => sum + (priceByIdRupees.get(id) ?? avgPriceRupees), 0);
    const remainingSlots = Math.max(0, bundleSize - picked.length);
    const raw = Math.round(pickedTotal + remainingSlots * avgPriceRupees);
    const discounted = Math.round(raw * (1 - meta.save / 100));
    if (amountEl) amountEl.textContent = formatBundleRupees(discounted);
    if (saveEl) saveEl.textContent = meta.save ? `You save ${formatBundleRupees(raw - discounted)}` : '';
    const remaining = bundleSize - picked.length;
    if (hintEl && addBtn) {
      if (remaining > 0) {
        hintEl.textContent = `Select ${remaining} more of ${bundleSize}`;
        addBtn.disabled = true;
        addBtn.textContent = `Choose ${bundleSize} blends to continue`;
      } else {
        hintEl.textContent = `All ${bundleSize} blends selected`;
        addBtn.disabled = false;
        addBtn.textContent = `Add bundle — ${formatBundleRupees(discounted)}`;
      }
    }
    renderGrid();
  };

  root.querySelector('[data-h9-packs]')?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-h9-pack]');
    if (!btn) return;
    root.querySelectorAll('[data-h9-pack]').forEach((b) => b.classList.remove('is-active'));
    btn.classList.add('is-active');
    bundleSize = Number(btn.dataset.size);
    picked = [];
    update();
  });

  addBtn?.addEventListener('click', async () => {
    if (picked.length !== bundleSize) return;
    const variantIds = picked.filter((id) => /^\d+$/.test(id)).map((id) => Number(id));
    if (!variantIds.length) return;
    addBtn.disabled = true;
    try {
      await addItemsToCart(variantIds.map((id) => ({ id, quantity: 1 })));
    } catch (err) {
      addBtn.textContent = err.message || 'Could not add bundle';
    } finally {
      addBtn.disabled = picked.length !== bundleSize;
    }
  });

  update();
}

function initDiscount(root) {
  if (!bindOnce(root)) return;
  const btn = root.querySelector('[data-h9-copy]');
  const code = root.querySelector('[data-h9-code]')?.textContent?.trim();
  btn?.addEventListener('click', async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch (e) {}
    const label = btn.textContent;
    btn.textContent = 'Copied ✓';
    setTimeout(() => {
      btn.textContent = label;
    }, 1800);
  });
}

function initHerbs(root) {
  if (!bindOnce(root)) return;
  const panel = root.querySelector('[data-h9-herb-panel]');
  const nameEl = root.querySelector('[data-h9-herb-name]');
  const descEl = root.querySelector('[data-h9-herb-desc]');
  root.querySelector('[data-h9-herb-grid]')?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-h9-herb]');
    if (!btn) return;
    if (nameEl) nameEl.textContent = btn.dataset.name;
    if (descEl) descEl.textContent = btn.dataset.desc;
    panel?.classList.add('is-open');
    panel?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  });
  root.querySelector('[data-h9-herb-close]')?.addEventListener('click', () => {
    panel?.classList.remove('is-open');
  });
}

function initTestimonials(root) {
  if (!bindOnce(root)) return;
  const slides = root.querySelectorAll('[data-h9-testi]');
  if (!slides.length) return;
  let i = 0;
  const show = (n) => {
    slides.forEach((s, idx) => s.classList.toggle('is-active', idx === n));
    i = n;
  };
  root.querySelector('[data-h9-testi-next]')?.addEventListener('click', () => show((i + 1) % slides.length));
  root.querySelector('[data-h9-testi-prev]')?.addEventListener('click', () =>
    show((i - 1 + slides.length) % slides.length)
  );
  setInterval(() => show((i + 1) % slides.length), 6000);
}

function initRewards(root) {
  if (!bindOnce(root)) return;
  const collected = new Set();
  const grid = root.querySelector('[data-h9-badges]');
  const fill = root.querySelector('[data-h9-progress]');
  const claim = root.querySelector('[data-h9-claim]');
  const count = Number(root.dataset.badgeCount || 9);
  const render = () => {
    if (!grid) return;
    grid.innerHTML = Array.from({ length: count })
      .map(
        (_, i) =>
          `<button type="button" class="h9-badge ${collected.has(i) ? 'is-collected' : ''}" data-i="${i}" aria-label="Herb card ${i + 1}">${
            collected.has(i) ? '✓' : i + 1
          }</button>`
      )
      .join('');
    grid.querySelectorAll('.h9-badge').forEach((b) => {
      b.addEventListener('click', () => {
        const n = Number(b.dataset.i);
        collected.has(n) ? collected.delete(n) : collected.add(n);
        render();
      });
    });
    const pct = Math.round((collected.size / count) * 100);
    if (fill) fill.style.width = `${pct}%`;
    if (claim) {
      const isLink = claim.tagName === 'A';
      if (isLink) {
        claim.style.pointerEvents = collected.size < count ? 'none' : '';
        claim.style.opacity = collected.size < count ? '0.45' : '';
      } else {
        claim.disabled = collected.size < count;
      }
    }
  };
  render();
}

function initFaq(root) {
  if (!bindOnce(root)) return;
  const list = root.querySelector('.h9-faq-list') || root;
  list.querySelectorAll('[data-h9-faq-item]').forEach((item) => {
    const q = item.querySelector('[data-h9-faq-q]');
    const a = item.querySelector('[data-h9-faq-a]');
    q?.addEventListener('click', () => {
      const isOpen = item.classList.contains('is-open');
      list.querySelectorAll('[data-h9-faq-item]').forEach((it) => {
        it.classList.remove('is-open');
        const ans = it.querySelector('[data-h9-faq-a]');
        if (ans) ans.style.maxHeight = null;
      });
      if (!isOpen) {
        item.classList.add('is-open');
        if (a) a.style.maxHeight = `${a.scrollHeight}px`;
      }
    });
  });
}

function initTotop(btn) {
  if (!bindOnce(btn)) return;
  window.addEventListener('scroll', () => {
    btn.classList.toggle('is-show', window.scrollY > 600);
  });
  btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
}

function initMomentJumps() {
  const shop = document.querySelector('[data-h9-shop]');
  document.querySelectorAll('[data-h9-moment-jump]').forEach((link) => {
    if (link.dataset.h9Bound === '1') return;
    link.dataset.h9Bound = '1';
    link.addEventListener('click', (e) => {
      if (!shop) return;
      const moment = link.getAttribute('data-h9-moment-jump');
      const tab = [...shop.querySelectorAll('[data-h9-moment]')].find((t) => t.dataset.h9Moment === moment);
      if (!tab) return;
      e.preventDefault();
      tab.click();
      const target = document.getElementById('moment');
      if (target) {
        const top = target.getBoundingClientRect().top + window.scrollY - 90;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    });
  });
}

function boot() {
  document.querySelectorAll('[data-h9-hero]').forEach(initHero);
  document.querySelectorAll('[data-h9-shop]').forEach(initShop);
  document.querySelectorAll('[data-h9-compare]').forEach(initCompare);
  document.querySelectorAll('[data-h9-bundle]').forEach(initBundle);
  document.querySelectorAll('[data-h9-discount]').forEach(initDiscount);
  document.querySelectorAll('[data-h9-herbs]').forEach(initHerbs);
  document.querySelectorAll('[data-h9-testimonials]').forEach(initTestimonials);
  document.querySelectorAll('[data-h9-rewards]').forEach(initRewards);
  document.querySelectorAll('[data-h9-faq]').forEach(initFaq);
  document.querySelectorAll('[data-h9-totop]').forEach(initTotop);
  initMomentJumps();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

document.addEventListener('shopify:section:load', boot);
